import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, FormControlLabel, Paper, Switch, TextField } from '@mui/material';
import { TablePagination } from '@mui/material';
import GetApp from '@mui/icons-material/GetApp';
import ProfitabilityList from './ProfitabilityList';
import ProfitabilityFetch from './ProfitabilityFetch';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import SearchField from '../ui/SearchField';
import PageTitle from '../ui/PageTitle';

import AppBindStyles from '../../AppBindStyles';
import { addDays, format, isValid, startOfDay, subMonths } from 'date-fns';

const abEnvironment = new AbEnvironment();
const ProfitabilityReportQuery = graphql`
query ProfitabilityReportQuery($startDate: Datetime, $endDate: Datetime, $appName: String, $accountName: String, $pAccountId: UUID, $customerOnly: Int, $first: Int, $offset: Int) {
  getbindcardchargesandpaymentswithsummary(startTs: $startDate, endTs: $endDate, appName: $appName, accountName: $accountName, pAccountId: $pAccountId, customerOnly: $customerOnly, first: $first, offset: $offset) {
    totalCount 
    ...ProfitabilityList_cl
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    flex: {
      flex: 1,
    },
    paper: {
      padding: theme.spacing(2),
      textAlign: 'left',
      color: theme.palette.text.secondary,
      maxWidth: 1200,
      marginTop: 16,
    }
});

class ProfitabilityReport extends Component {

  constructor(props) {
    super(props);

    let cToDate = new Date();
    let tDate = localStorage.getItem('prToDate');
    if (tDate != null) {
      cToDate = new Date(tDate);
    }
    //search to date is "start of next day"
    let searchToDateUtc = startOfDay(addDays(cToDate, 1)).toISOString();

    let cFromDate = new Date();
    let fDate = localStorage.getItem('prFromDate');
    if (fDate != null) {
      cFromDate = new Date(fDate);
    } else {
      cFromDate = subMonths(cToDate, 1);
    }
    //search from date is "start of day"
    let searchFromDateUtc = startOfDay(cFromDate).toISOString();

    let customerOnly = 1;
    if (localStorage.getItem('prCustomerOnly')) {
      customerOnly = parseInt(localStorage.getItem('prCustomerOnly'), 10);
    }

    this.state = {
      startDate: cFromDate,
      queryStartDateUtc: searchFromDateUtc,
      endDate: cToDate,
      queryEndDateUtc: searchToDateUtc,
      appName: '', 
      accountName: '',
      customerOnly: customerOnly, 
      pAccountId: abEnvironment.getUserAttribute('a_id'),
      page: 0,
      rowsPerPage: 10,
    };  
  }

  handleChange = name => event => {
    this.setState({ 
      [name]: event.target.value,
      page: 0,
    });
  };

  setFromDate(theDate) {
    if (isValid(theDate)) {
      let searchDateUtc = startOfDay(new Date(theDate)).toISOString();
      localStorage.setItem('prFromDate', theDate);
      this.setState({ page : 0, startDate: theDate, queryStartDateUtc: searchDateUtc });
    }
  }

  setToDate(theDate) {
    if (isValid(theDate)) {
      let searchDateUtc = startOfDay(addDays(new Date(theDate), 1)).toISOString();
      localStorage.setItem('prToDate', theDate);
      this.setState({ page : 0, endDate: theDate, queryEndDateUtc: searchDateUtc });
    }
  }

  handleChangePage = (event, page) => {
    this.setState({page: page});
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page : 0});
  }

  // The download needs to do another query in case there is more data for
  // the range than is shown on the screen.
  downloadCsv(startDate, endDate, appName, accountName, pAccountId, customerOnly, first, offset) {

    const { logError } = this.props.auth;

    ProfitabilityFetch.fetch(startDate, endDate, appName, accountName, pAccountId, customerOnly, first, offset).toPromise()
    .then(pf => {
      let theCount = pf.getbindcardchargesandpaymentswithsummary.totalCount;
      if (theCount < 1) {
        return;
      }
      let thenodes = pf.getbindcardchargesandpaymentswithsummary.nodes;
      let csv;

      csv = 'Date, Customer, App, Billed To Customer, Billed to Me, App Charge, Markup, Fees (AppBind), Fees (Network), Rebate, Profit\n';
      thenodes.map(n=> {
        //transaction time is non-zoned, specify as UTC
        let tdate = format(new Date(n.transactionTime+'Z'), 'yyyy-MM-dd');
        let bcc = Number(n.bindCardCharge)
                  .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        let mkp = '';
        if (n.markup !== 0)
        {
          mkp = Number(n.markup)
                  .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        }
        let billToCust = 0;
        let billToMe = 0;
        let ppFeeCust = 0;
        let ppFeeRes = 0;
        let ppFeeTot = 0;

        let thePayments = n.payments;
        if (thePayments != null && thePayments.length > 0)
        {
          for (let i=0; i<thePayments.length; i++)
          {
            if (thePayments[i].psAccountId === n.customerAccountId)
            {
              billToCust = Number(billToCust) + Number(thePayments[i].paidAmount);
              ppFeeCust = Number(ppFeeCust) + Number(thePayments[i].ppAmount);
            }
            else if (thePayments[i].psAccountId === n.provisioningAccountId)
            {
              billToMe = Number(billToMe) + Number(thePayments[i].paidAmount);
              ppFeeRes = Number(ppFeeRes) + Number(thePayments[i].ppAmount);
            }
          }
        }

        let billedToCust = '';
        if (billToCust > 0) {
          billedToCust = Number(billToCust)
                          .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        }

        ppFeeTot = Number(ppFeeCust) + Number(ppFeeRes);
        let appBindFee = Number(n.appbindFee)
        .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        let ppFee = Number(ppFeeTot)
        .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

        let rebate = '';
        if (Number(n.rebate) > 0)
        {
          rebate = Number(n.rebate)
            .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        }

        let netToMe = '';
        if (Number(n.markup) > 0)
        {
          netToMe = Number(n.profit)
            .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
        }
        let csvline = '' + tdate + ',' + n.customerAccountName + ',' + n.appName + ','
                    + billedToCust + ',' + billToMe + ','
                    + bcc + ',' + mkp + ',' 
                    + appBindFee + ',' + ppFee + ',' + rebate + ','
                    + netToMe + '\n';
        csv = csv + csvline;
        return true;
      });

      var hiddenElement = document.createElement('a');
      hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);
      hiddenElement.target = '_blank';
      hiddenElement.download = 'profitability.csv';
      hiddenElement.click();
    })
    .catch(err => {
      // alert('Error: ' + err);
      logError('Exception in ProfitabilityReport.js: ' + err);
    });
  }

  render() {
    const { isAuthenticated, isAuthenticatedCustomer, isUnauthenticatedCustomer, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticated() || needAssent() || isUnauthenticatedCustomer()) { 
      return ( <Redirect to='/home' /> );
    }
    
    let theAppSearch = '%';
    if (this.state.appName !== '')
    {
      theAppSearch = '%' + this.state.appName + '%';
    }

    let theAccountSearch = '%';
    if (this.state.accountName !== '')
    {
      theAccountSearch = '%' + this.state.accountName + '%';
    }

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0)
    {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;

    let totalCount = 0;

    let theRole = "reseller";
    if (isAuthenticatedCustomer()) {
      theRole = "customer";
    }

    let thePage = this.props.pg;
    if (thePage == null)
      thePage = "custCharge";

  
    return (
      <div className={classes.root}>

        <PageTitle title={"Profitability report"} subtitle={"Review markups and rebates by date range."}/>

        <Paper className={classes.paper}>
        <Grid container spacing={2} alignItems='center' style={AppBindStyles.searchContainerOnPaper}>
          <Grid item>
            <SearchField
              id="customerName"
              label="Search by customer name"
              value={this.state.accountName}
              onChange={this.handleChange('accountName')}
            />
          </Grid>
          <Grid item>
            <SearchField
              id="appName"
              label="Search by app name"
              value={this.state.appName}
              onChange={this.handleChange('appName')}
            />
          </Grid>
          <Grid item>
            <div style={AppBindStyles.datePickerField}>
            <DatePicker
              maxDateMessage={AppBindStyles.datePickerMessageMax}
              autoOk
              label="From date"
              value={this.state.startDate}
              onChange={(date) => this.setFromDate(date) }
              format="YYYY-MM-DD"
              disableFuture
              renderInput={(params) => <TextField {...params} />}
            />
            </div>
          </Grid>
          <Grid item>
            <div style={AppBindStyles.datePickerField}>
            <DatePicker
              autoOk
              label="To date"
              value={this.state.endDate}
              onChange={(date) => this.setToDate(date) }
              format="YYYY-MM-DD"
              disableFuture
              renderInput={(params) => <TextField {...params} />}
            />
            </div>
          </Grid>
          <Grid item >
            <FormControlLabel
              control={
                <Switch
                  checked={this.state.customerOnly === 1}
                   onChange={(e, checked) => {
                    if (checked) {
                      localStorage.setItem('prCustomerOnly', 1);
                      this.setState({ page : 0, customerOnly: 1});
                    } else {
                      localStorage.setItem('prCustomerOnly', 0);
                      this.setState({ page : 0, customerOnly: 0});
                    }
                  }}
                />
              }
              label={"Only show subscriptions that are billed to my customer"}
            />
          </Grid>

          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onClick={() => 
                this.downloadCsv( 
                                this.state.queryStartDateUtc, this.state.queryEndDateUtc, theAppSearch,
                                this.state.accountName, this.state.pAccountId, 
                                this.state.customerOnly, 10000, 0)}
            >
              Download Report&nbsp;&nbsp;<GetApp/>
            </Button>
          </Grid>
        </Grid>
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={ProfitabilityReportQuery}
          variables={{startDate: this.state.queryStartDateUtc,
                      endDate: this.state.queryEndDateUtc,
                      appName: theAppSearch,
                      accountName: theAccountSearch,
                      pAccountId: this.state.pAccountId,
                      customerOnly: this.state.customerOnly,
                      first: first,
                      offset: offset}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              // Determine offset information
              totalCount = props.getbindcardchargesandpaymentswithsummary.totalCount;

              let showCustomerName = false;
              if (this.state.pAccountId != null)
                showCustomerName = true;

              return (
                <div>
                  <ProfitabilityList cl={props.getbindcardchargesandpaymentswithsummary} 
                               thePage={thePage}
                               theRole={theRole}
                               sn={showCustomerName}
                               history={this.props.history}/>
                  <div style={AppBindStyles.paginationTableFooter}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={this.state.rowsPerPage}
                      page={this.state.page}
                      backIconButtonProps={{
                        'aria-label': 'Previous Page',
                      }}
                      nextIconButtonProps={{
                        'aria-label': 'Next Page',
                      }}
                      onPageChange={this.handleChangePage}
                      onRowsPerPageChange={this.handleChangeRowsPerPage}
                    />                        
                  </div>
                </div>
              )
            }
            return (
              <LoadingSpinner />
            );
          }}
        />
         </Paper>
      </div>
    );
  }
}
  
ProfitabilityReport.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(ProfitabilityReport);
